/**
 * AdminGroupSessionsTab Component
 *
 * Manages group session scheduling:
 * - View / schedule / edit / delete sessions (with optional recurrence)
 * - Mark sessions complete
 * - Manually push email notification (with optional custom message)
 * - Manage custom session types (add / archive)
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Calendar, Plus, Trash2, CheckCircle2, Mail, Settings, Repeat } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_OPTIONS = [
  { value: 'inner_circle', label: 'Inner Circle' },
  { value: 'inner_edge', label: 'Inner Edge' },
  { value: 'coaching_activation', label: 'Coaching Activation' },
  { value: 'costa_rica', label: 'Retreat' },
  { value: 'sdc', label: 'SDC' },
];

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
];

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'UTC', label: 'UTC' },
];

const TZ_LABELS = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  UTC: 'UTC',
};

const formatTimeRange = (session) => {
  const t = session?.scheduled_time;
  const dur = parseInt(session?.duration_minutes || 60, 10);
  const tzLabel = TZ_LABELS[session?.time_zone] || session?.time_zone || 'ET';
  if (!t) return tzLabel;
  const [h, m] = t.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return `${t} ${tzLabel}`;
  const totalEnd = h * 60 + m + dur;
  const fmt = (mins) => {
    const hh = Math.floor((mins % (24 * 60)) / 60);
    const mm = mins % 60;
    const ampm = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 || 12;
    return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
  };
  return `${fmt(h * 60 + m)} – ${fmt(totalEnd)} ${tzLabel}`;
};

const emptySessionForm = () => ({
  title: '',
  description: '',
  session_type: '',
  scheduled_date: '',
  scheduled_time: '',
  time_zone: 'America/New_York',
  duration_minutes: 60,
  zoom_link: 'https://us06web.zoom.us/my/crystalanddrnicole',
  zoom_password: '1111',
  assigned_tiers: [],
  facilitators: 'Crystal Khalil, Dr. Nicole LaBeach',
  custom_message: '',
  // Recurrence
  is_recurring: false,
  recurrence_frequency: 'weekly',
  recurrence_count: 12,
});

const AdminGroupSessionsTab = () => {
  const [groupSessions, setGroupSessions] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [sessionForm, setSessionForm] = useState(emptySessionForm());

  // Notify-members dialog
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyTarget, setNotifyTarget] = useState(null);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifying, setNotifying] = useState(false);

  // Manage Types dialog
  const [typesOpen, setTypesOpen] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState('');

  // Reminder open-rate stats { [sessionId]: { same_day: {...}, starting_soon: {...} } }
  const [reminderStats, setReminderStats] = useState({});

  useEffect(() => {
    fetchSessions();
    fetchSessionTypes();
  }, []);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/group-sessions`, {
        headers: authHeaders(),
      });
      setGroupSessions(res.data);
      // Pull reminder stats for each session (parallel, best-effort)
      const list = res.data || [];
      const entries = await Promise.all(
        list.map(async (s) => {
          try {
            const r = await axios.get(
              `${API_URL}/api/admin/group-sessions/${s.id}/reminder-stats`,
              { headers: authHeaders() }
            );
            return [s.id, r.data];
          } catch {
            return [s.id, null];
          }
        })
      );
      setReminderStats(Object.fromEntries(entries.filter(([, v]) => v)));
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/group-session-types`, {
        headers: authHeaders(),
      });
      const types = res.data?.types || [];
      setSessionTypes(types);
      // Default the form to the first type if not set
      setSessionForm((prev) =>
        prev.session_type ? prev : { ...prev, session_type: types[0]?.value || '' }
      );
    } catch (error) {
      console.error('Error fetching session types:', error);
      toast.error('Failed to load session types');
    }
  };

  const handleSaveSession = async () => {
    if (!sessionForm.title || !sessionForm.scheduled_date || !sessionForm.scheduled_time) {
      toast.error('Title, date, and time are required');
      return;
    }
    if (sessionForm.is_recurring && !editingSession) {
      const c = parseInt(sessionForm.recurrence_count, 10);
      if (!c || c < 1 || c > 104) {
        toast.error('Recurrence count must be between 1 and 104');
        return;
      }
    }

    try {
      const facilitatorsValue = sessionForm.facilitators;
      const facilitators = Array.isArray(facilitatorsValue)
        ? facilitatorsValue
        : (facilitatorsValue || '').split(',').map((f) => f.trim()).filter(Boolean);

      const payload = {
        title: sessionForm.title,
        description: sessionForm.description,
        session_type: sessionForm.session_type,
        scheduled_date: sessionForm.scheduled_date,
        scheduled_time: sessionForm.scheduled_time,
        time_zone: sessionForm.time_zone,
        duration_minutes: sessionForm.duration_minutes,
        zoom_link: sessionForm.zoom_link,
        zoom_password: sessionForm.zoom_password,
        assigned_tiers: sessionForm.assigned_tiers,
        facilitators,
      };

      if (!editingSession) {
        if (sessionForm.custom_message?.trim()) {
          payload.custom_message = sessionForm.custom_message.trim();
        }
        if (sessionForm.is_recurring) {
          payload.recurrence = {
            frequency: sessionForm.recurrence_frequency,
            count: parseInt(sessionForm.recurrence_count, 10),
          };
        }
        const res = await axios.post(`${API_URL}/api/admin/group-sessions`, payload, {
          headers: authHeaders(),
        });
        const occurrences = res.data?.occurrences || 1;
        const total = res.data?.email_stats?.total_targeted ?? 0;
        toast.success(
          occurrences > 1
            ? `Created ${occurrences} sessions; queued emails for ${total} member${total === 1 ? '' : 's'}`
            : `Session scheduled; queued email for ${total} member${total === 1 ? '' : 's'}`
        );
      } else {
        await axios.put(`${API_URL}/api/admin/group-sessions/${editingSession.id}`, payload, {
          headers: authHeaders(),
        });
        toast.success('Session updated successfully');
      }

      setShowSessionDialog(false);
      setEditingSession(null);
      setSessionForm({ ...emptySessionForm(), session_type: sessionTypes[0]?.value || '' });
      fetchSessions();
    } catch (error) {
      const status = error.response?.status;
      const detail =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        (typeof error.response?.data === 'string' ? error.response.data : null) ||
        error.message ||
        'Unknown error';
      const friendly = status
        ? `Failed to save session (HTTP ${status}): ${detail}`
        : `Failed to save session: ${detail}`;
      console.error('[GroupSessions] Save failed', { status, detail, error });
      toast.error(friendly);
    }
  };

  const handleMarkSessionComplete = async (sessionId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/group-sessions/${sessionId}/complete`,
        {},
        { headers: authHeaders() }
      );
      toast.success('Session marked as complete');
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark session complete');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/group-sessions/${sessionId}`, {
        headers: authHeaders(),
      });
      toast.success('Session deleted');
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete session');
    }
  };

  const openNotifyDialog = (session) => {
    setNotifyTarget(session);
    setNotifyMessage('');
    setNotifyOpen(true);
  };

  const handleSendNotify = async () => {
    if (!notifyTarget) return;
    setNotifying(true);
    try {
      const body = notifyMessage.trim() ? { custom_message: notifyMessage.trim() } : {};
      const res = await axios.post(
        `${API_URL}/api/admin/group-sessions/${notifyTarget.id}/notify`,
        body,
        { headers: authHeaders() }
      );
      const total = res.data?.total_targeted ?? 0;
      toast.success(`Queued email for ${total} member${total === 1 ? '' : 's'}`);
      setNotifyOpen(false);
      setNotifyTarget(null);
      setNotifyMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notifications');
    } finally {
      setNotifying(false);
    }
  };

  const handleAddType = async () => {
    const label = newTypeLabel.trim();
    if (!label) {
      toast.error('Enter a label first');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/admin/group-session-types`,
        { label },
        { headers: authHeaders() }
      );
      toast.success(`Added "${label}"`);
      setNewTypeLabel('');
      fetchSessionTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add type');
    }
  };

  const handleDeleteType = async (typeId, label) => {
    if (!window.confirm(`Archive "${label}"? Existing sessions of this type are not affected.`))
      return;
    try {
      await axios.delete(`${API_URL}/api/admin/group-session-types/${typeId}`, {
        headers: authHeaders(),
      });
      toast.success(`Archived "${label}"`);
      fetchSessionTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive type');
    }
  };

  const handleTierToggle = (tierValue) => {
    setSessionForm((prev) => {
      const tiers = prev.assigned_tiers.includes(tierValue)
        ? prev.assigned_tiers.filter((t) => t !== tierValue)
        : [...prev.assigned_tiers, tierValue];
      return { ...prev, assigned_tiers: tiers };
    });
  };

  const handleBulkDelete = async () => {
    const days = window.prompt(
      'Delete past sessions older than how many days?\n\nEnter a number (default 30). Sessions scheduled within that window will NOT be deleted.',
      '30'
    );
    if (days === null) return;
    const parsedDays = parseInt(days, 10);
    if (!parsedDays || parsedDays < 1) {
      toast.error('Please enter a positive number of days.');
      return;
    }
    if (
      !window.confirm(
        `Delete ALL group sessions whose scheduled date is more than ${parsedDays} days ago?\n\nThis cannot be undone.`
      )
    )
      return;

    try {
      const res = await axios.delete(
        `${API_URL}/api/admin/group-sessions?older_than_days=${parsedDays}`,
        { headers: authHeaders() }
      );
      const count = res.data?.deleted ?? 0;
      toast.success(
        count === 0
          ? `No sessions older than ${parsedDays} days to delete.`
          : `Deleted ${count} session${count === 1 ? '' : 's'} older than ${parsedDays} days.`
      );
      fetchSessions();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Bulk delete failed: ${detail}`);
    }
  };

  return (
    <>
      <Card className="card-base">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Group Sessions
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setTypesOpen(true)}
                data-testid="admin-manage-session-types-btn"
                title="Add or archive session types"
              >
                <Settings className="h-4 w-4 mr-2" /> Manage Types
              </Button>
              <Button
                variant="outline"
                onClick={handleBulkDelete}
                data-testid="admin-bulk-delete-sessions-btn"
                title="Delete past sessions older than N days"
                className="border-red-200 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Cleanup Old
              </Button>
              <Button
                onClick={() => {
                  setEditingSession(null);
                  setSessionForm({
                    ...emptySessionForm(),
                    session_type: sessionTypes[0]?.value || '',
                  });
                  setShowSessionDialog(true);
                }}
                className="btn-primary"
                data-testid="admin-schedule-session-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Schedule Session
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest mx-auto"></div>
            </div>
          ) : groupSessions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No group sessions scheduled yet.</p>
          ) : (
            <div className="space-y-4">
              {groupSessions.map((session) => (
                <div key={session.id} className="p-4 border border-sand rounded-xl">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-medium text-forest">{session.title}</h3>
                        <Badge
                          className={
                            session.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }
                        >
                          {session.status}
                        </Badge>
                        <Badge variant="outline">
                          {session.session_type?.replace(/_/g, ' ')}
                        </Badge>
                        {session.recurrence_id && (
                          <Badge variant="outline" className="text-purple-700 border-purple-300">
                            <Repeat className="h-3 w-3 mr-1" /> Recurring
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{session.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span>
                          <Calendar className="h-4 w-4 inline mr-1" />
                          {session.scheduled_date} &middot; {formatTimeRange(session)}
                        </span>
                        <span>Duration: {session.duration_minutes} min</span>
                        <span>
                          Facilitators:{' '}
                          {Array.isArray(session.facilitators)
                            ? session.facilitators.join(', ')
                            : session.facilitators}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {session.assigned_tiers?.map((tier) => (
                          <Badge key={tier} variant="outline" className="text-xs">
                            {tier.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                      {reminderStats[session.id] && (
                        (reminderStats[session.id].same_day.sent > 0 ||
                          reminderStats[session.id].starting_soon.sent > 0) && (
                          <div
                            className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600"
                            data-testid={`reminder-stats-${session.id}`}
                          >
                            {reminderStats[session.id].same_day.sent > 0 && (
                              <Badge variant="outline" className="border-forest/40 text-forest font-normal">
                                3–4h reminder · {reminderStats[session.id].same_day.opened}/
                                {reminderStats[session.id].same_day.sent} opened
                                {' '}({reminderStats[session.id].same_day.open_rate}%)
                              </Badge>
                            )}
                            {reminderStats[session.id].starting_soon.sent > 0 && (
                              <Badge variant="outline" className="border-terracotta/40 text-terracotta font-normal">
                                30-min reminder · {reminderStats[session.id].starting_soon.opened}/
                                {reminderStats[session.id].starting_soon.sent} opened
                                {' '}({reminderStats[session.id].starting_soon.open_rate}%)
                              </Badge>
                            )}
                          </div>
                        )
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap justify-end">
                      {session.status !== 'completed' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600"
                            onClick={() => openNotifyDialog(session)}
                            data-testid={`notify-members-btn-${session.id}`}
                            title="Send email to all targeted members"
                          >
                            <Mail className="h-3 w-3 mr-1" /> Notify
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600"
                            onClick={() => handleMarkSessionComplete(session.id)}
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Complete
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => handleDeleteSession(session.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ Schedule / Edit Session Dialog ============ */}
      <Dialog open={showSessionDialog} onOpenChange={setShowSessionDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSession ? 'Edit Session' : 'Schedule New Group Session'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={sessionForm.title}
                onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })}
                placeholder="Power & Presence Session 1"
                data-testid="session-title-input"
              />
            </div>
            <div>
              <Label>Session Type</Label>
              <Select
                value={sessionForm.session_type}
                onValueChange={(v) => setSessionForm({ ...sessionForm, session_type: v })}
              >
                <SelectTrigger data-testid="session-type-select">
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                  {sessionTypes.map((type) => (
                    <SelectItem key={type.id || type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={sessionForm.description}
                onChange={(e) => setSessionForm({ ...sessionForm, description: e.target.value })}
                placeholder="Session details..."
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={sessionForm.scheduled_date}
                  onChange={(e) =>
                    setSessionForm({ ...sessionForm, scheduled_date: e.target.value })
                  }
                  data-testid="session-date-input"
                />
              </div>
              <div>
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={sessionForm.scheduled_time}
                  onChange={(e) =>
                    setSessionForm({ ...sessionForm, scheduled_time: e.target.value })
                  }
                  data-testid="session-time-input"
                />
              </div>
              <div>
                <Label>Time Zone</Label>
                <Select
                  value={sessionForm.time_zone}
                  onValueChange={(v) => setSessionForm({ ...sessionForm, time_zone: v })}
                >
                  <SelectTrigger data-testid="session-tz-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Duration (minutes)</Label>
              <Input
                type="number"
                min={15}
                max={480}
                value={sessionForm.duration_minutes}
                onChange={(e) =>
                  setSessionForm({
                    ...sessionForm,
                    duration_minutes: parseInt(e.target.value, 10) || 60,
                  })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Zoom Link</Label>
                <Input
                  value={sessionForm.zoom_link}
                  onChange={(e) => setSessionForm({ ...sessionForm, zoom_link: e.target.value })}
                />
              </div>
              <div>
                <Label>Zoom Password</Label>
                <Input
                  value={sessionForm.zoom_password}
                  onChange={(e) =>
                    setSessionForm({ ...sessionForm, zoom_password: e.target.value })
                  }
                />
              </div>
            </div>
            <div>
              <Label>Facilitators (comma-separated)</Label>
              <Input
                value={sessionForm.facilitators}
                onChange={(e) =>
                  setSessionForm({ ...sessionForm, facilitators: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Assign to Tiers</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TIER_OPTIONS.map((tier) => (
                  <label
                    key={tier.value}
                    className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-sand/50"
                  >
                    <input
                      type="checkbox"
                      checked={sessionForm.assigned_tiers.includes(tier.value)}
                      onChange={() => handleTierToggle(tier.value)}
                    />
                    <span className="text-sm">{tier.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* ============ Recurrence (only for new sessions) ============ */}
            {!editingSession && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Repeat className="h-4 w-4" /> Make this a recurring series
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">
                      e.g. Reset Room weekly. We'll generate every occurrence and email
                      members one summary listing all dates.
                    </p>
                  </div>
                  <Switch
                    checked={sessionForm.is_recurring}
                    onCheckedChange={(v) =>
                      setSessionForm({ ...sessionForm, is_recurring: v })
                    }
                    data-testid="recurring-toggle"
                  />
                </div>
                {sessionForm.is_recurring && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label>Frequency</Label>
                      <Select
                        value={sessionForm.recurrence_frequency}
                        onValueChange={(v) =>
                          setSessionForm({ ...sessionForm, recurrence_frequency: v })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENCY_OPTIONS.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Number of occurrences</Label>
                      <Input
                        type="number"
                        min={1}
                        max={104}
                        value={sessionForm.recurrence_count}
                        onChange={(e) =>
                          setSessionForm({ ...sessionForm, recurrence_count: e.target.value })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ============ Optional custom message in announcement email ============ */}
            {!editingSession && (
              <div>
                <Label>Custom message (optional, included in announcement email)</Label>
                <Textarea
                  rows={3}
                  value={sessionForm.custom_message}
                  onChange={(e) =>
                    setSessionForm({ ...sessionForm, custom_message: e.target.value })
                  }
                  placeholder="Anything you'd like members to know up front..."
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowSessionDialog(false)}>
              Cancel
            </Button>
            <Button
              className="btn-primary"
              onClick={handleSaveSession}
              data-testid="save-session-btn"
            >
              {editingSession ? 'Update Session' : 'Schedule Session'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ Notify Members Dialog ============ */}
      <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Notify members</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-600">
              Send an email to every member targeted by{' '}
              <strong>{notifyTarget?.title}</strong>.
            </p>
            <div>
              <Label>Custom message (optional)</Label>
              <Textarea
                rows={4}
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                placeholder="Anything you'd like to add to the announcement..."
                data-testid="notify-custom-message"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to send the standard announcement.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setNotifyOpen(false)} disabled={notifying}>
              Cancel
            </Button>
            <Button
              className="btn-primary"
              onClick={handleSendNotify}
              disabled={notifying}
              data-testid="send-notify-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              {notifying ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ Manage Session Types Dialog ============ */}
      <Dialog open={typesOpen} onOpenChange={setTypesOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage session types</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Input
                value={newTypeLabel}
                onChange={(e) => setNewTypeLabel(e.target.value)}
                placeholder="e.g. Reset Room Live"
                data-testid="new-session-type-input"
              />
              <Button onClick={handleAddType} data-testid="add-session-type-btn">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
              {sessionTypes.length === 0 ? (
                <p className="text-sm text-gray-500 p-3">No types yet.</p>
              ) : (
                sessionTypes.map((t) => (
                  <div key={t.id || t.value} className="flex items-center justify-between p-2">
                    <div>
                      <div className="text-sm font-medium">{t.label}</div>
                      <div className="text-xs text-gray-500">{t.value}</div>
                    </div>
                    {t.is_default ? (
                      <span className="text-xs text-gray-400">default</span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => handleDeleteType(t.id, t.label)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
            <p className="text-xs text-gray-500">
              Default types can't be deleted (they keep the dropdown safe). Custom types you
              add can be archived. Existing sessions of an archived type are unaffected.
            </p>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setTypesOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminGroupSessionsTab;
